import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { useAuthorizedQuery } from "../api/authorized-query";
import { seedgreenAPI } from "../api/axios";
import { RanchSeasonDTO } from "../dtos/RanchSeasonDTO";
import { STALE_TIME_MEDIUM } from "../queries/query.constants";
import { computed } from "vue";
const RANCH_SEASON_STALE_TIME = STALE_TIME_MEDIUM;
export function useRanchSeasons(seasonId) {
    const isAuthorized = useAuthorizedQuery();
    const query = useQuery({
        enabled: isAuthorized,
        queryKey: ["ranch-seasons", seasonId],
        queryFn: async () => {
            const params = seasonId?.value ? `?seasonId=${seasonId.value}` : "";
            const response = await seedgreenAPI.get(`/v2/RanchSeason${params}`);
            return response.data;
        },
        staleTime: RANCH_SEASON_STALE_TIME,
        select: (data) => {
            try {
                return (data?.map((ranchSeason) => new RanchSeasonDTO(ranchSeason)) ?? []);
            }
            catch (error) {
                console.error("query/ranch-seasons select ERROR", error);
                return [];
            }
        },
    });
    return {
        ranchSeasons: query.data,
        ...query,
    };
}
export function useCreateRanchSeason() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const query = useMutation({
        mutationFn: async (dto) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.post("/v2/RanchSeason", dto);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate relevant queries
            queryClient.invalidateQueries({ queryKey: ["ranch-seasons"] });
            queryClient.invalidateQueries({
                queryKey: ["harvest-overview-ranches"],
            });
        },
    });
    return {
        createRanchSeason: query.mutateAsync,
        ...query,
    };
}
export function useUpdateRanchSeason() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const query = useMutation({
        mutationFn: async ({ id, coolerId, organicStatusId, ranchCode, }) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.put(`/v2/RanchSeason/${id}`, {
                coolerId,
                organicStatusId,
                ranchCode,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["ranch-seasons"] });
            queryClient.invalidateQueries({
                queryKey: ["harvest-overview-ranches"],
            });
        },
    });
    return {
        updateRanchSeason: query.mutateAsync,
        ...query,
    };
}
// Returns a mapping of seasonId -> number of ranch-season mappings (not distinct ranches)
export function useRanchSeasonCounts() {
    const { ranchSeasons, isPending, isError } = useRanchSeasons();
    const counts = computed(() => {
        const seasonToRanchSet = new Map();
        for (const rs of ranchSeasons.value || []) {
            const set = seasonToRanchSet.get(rs.seasonId) || new Set();
            set.add(rs.ranchId);
            seasonToRanchSet.set(rs.seasonId, set);
        }
        const result = {};
        for (const [seasonId, set] of seasonToRanchSet) {
            result[seasonId] = set.size;
        }
        return result;
    });
    return {
        counts,
        isPending,
        isError,
    };
}
